/**
 * The worked example board.
 *
 * Carried over from the design prototype: a website redesign mapped across three
 * framed areas, so a first-time user lands on a board that already shows what
 * the tool is for rather than an empty void.
 *
 * Lives in the shared package because two very different things need it — the
 * server seeds it into every new account, and the client builds the signed-out
 * demo sandbox from it without touching the server at all.
 */
/** Inline placeholder artwork, so the seed needs no binary assets. */
function placeholder(label, w, h) {
    const escaped = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const palette = ['#D9C8AE', '#B66B4F', '#667462', '#292F2C', '#EBE2D2'];
    let artwork;
    if (label === 'palette') {
        artwork = palette.map((fill, i) => `<rect x='${i * 128}' width='128' height='420' fill='${fill}'/>`).join('');
    }
    else if (label === 'type specimen') {
        artwork = `<rect width='640' height='420' fill='#EBE2D2'/><text x='180' y='190' font-family='Georgia,serif' font-size='146' fill='#292F2C'>Aa.</text><text x='180' y='250' font-family='sans-serif' font-size='21' letter-spacing='5' fill='#292F2C'>SLOW / SIMPLE / HUMAN</text>`;
    }
    else if (label === 'texture') {
        artwork =
            `<rect width='640' height='420' fill='#D9C8AE'/>` +
                Array.from({ length: 22 }, (_, i) => `<path d='M${i * 36 - 150} 0q240 220 0 420' fill='none' stroke='#8B775E' stroke-opacity='.45' stroke-width='3'/>`).join('');
    }
    else if (label.includes('hero') || label.includes('product')) {
        artwork = `<rect width='640' height='420' fill='#EBE2D2'/><circle cx='495' cy='105' r='74' fill='#D6AC81'/><path d='M0 330q150-130 320-30t320-20v140H0' fill='#667462'/><ellipse cx='310' cy='357' rx='105' ry='18' fill='#292F2C' opacity='.2'/><path d='M235 350V163a75 75 0 0 1 150 0v187z' fill='#B66B4F'/><path d='M262 350V168a48 48 0 0 1 96 0v182z' fill='#D9C8AE'/><path d='M286 350V189a24 24 0 0 1 48 0v161z' fill='#292F2C'/>`;
    }
    else if (label.includes('logo') || label.includes('mark') || label.includes('favicon')) {
        artwork = `<rect width='640' height='420' fill='#292F2C'/><path d='M240 260V160h80v100h80V160' fill='none' stroke='#D9C8AE' stroke-width='28' stroke-linejoin='round'/>`;
    }
    else {
        artwork = `<rect width='640' height='420' fill='#292F2C'/><rect x='40' y='70' width='145' height='16' rx='8' fill='#D9C8AE'/><path d='M40 135h560M40 155h340' stroke='#667462' stroke-width='8'/><rect x='40' y='225' width='130' height='48' rx='24' fill='#B66B4F'/>`;
    }
    if (label === 'hero image')
        artwork += `<rect y='240' width='640' height='180' fill='#292F2C' opacity='.85'/>`;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}' viewBox='0 0 640 420' preserveAspectRatio='xMidYMid slice'>${artwork}<title>${escaped}</title></svg>`;
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}
export const DEMO_BOARD_NAME = 'Nova website — redesign';
export function demoDoc() {
    const items = [];
    let z = 1000;
    const nextZ = () => z++;
    const frame = (id, x, y, w, h, label, zz) => items.push({ id, type: 'frame', x, y, w, h, label, z: zz });
    const image = (id, x, y, w, h, label, pw, ph) => items.push({ id, type: 'image', x, y, w, h, src: placeholder(label, pw, ph), name: label, z: nextZ() });
    const sticky = (id, x, y, color, text) => items.push({ id, type: 'sticky', x, y, w: 180, h: 180, color, text, z: nextZ() });
    const text = (id, x, y, w, h, size, body) => items.push({ id, type: 'text', x, y, w, h, size, text: body, z: nextZ() });
    frame('f1', 0, 0, 1180, 740, 'Moodboard', 1);
    frame('f2', 0, 820, 560, 540, 'Copy & voice', 2);
    frame('f3', 1560, 0, 920, 1180, 'Layout mock — home', 3);
    image('m1', 40, 64, 470, 300, 'hero shot', 640, 420);
    image('m2', 540, 64, 290, 300, 'texture', 420, 440);
    image('m3', 860, 64, 280, 300, 'type specimen', 420, 440);
    sticky('m4', 40, 404, '#F6D56A', 'Warm, tactile, hand-made feel');
    sticky('m5', 244, 436, '#F49E9E', 'Big serif headlines?');
    sticky('m6', 448, 404, '#9BD8AC', 'Nav stays ultra simple — 3 links, no dropdowns');
    image('m7', 660, 404, 250, 164, 'palette', 420, 280);
    items.push({
        id: 'm8',
        type: 'link',
        x: 924,
        y: 404,
        w: 220,
        h: 88,
        title: 'Inspo — minimal portfolios',
        url: 'gallery.example/minimal',
        z: nextZ(),
    });
    text('m9', 660, 596, 470, 110, 'm', 'Direction: analog warmth. Generous whitespace, one accent color, photography over illustration.');
    text('c1', 40, 868, 480, 150, 'l', 'Tools for slow makers.');
    text('c2', 40, 1020, 470, 110, 'm', 'Scribbleplot keeps every sketch, swatch, and stray thought on one board, so the layout designs itself.');
    text('c3', 40, 1140, 290, 50, 's', 'CTA: Start a board — free');
    sticky('c4', 336, 1148, '#8EC1F2', 'Tone: quiet confidence, zero hype');
    image('a1', 1240, 660, 220, 160, 'logo — primary', 360, 260);
    const groupChildren = [
        ['logo mono', 0, 0],
        ['mark only', 230, 0],
        ['wordmark', 0, 160],
        ['favicon', 230, 160],
    ].map(([name, rx, ry], i) => ({
        id: `gc${i}`,
        type: 'image',
        rx,
        ry,
        w: 210,
        h: 150,
        src: placeholder(name, 360, 260),
        name,
    }));
    items.push({
        id: 'g1',
        type: 'group',
        x: 1252,
        y: 880,
        w: 200,
        h: 76,
        name: 'Logo pack',
        children: groupChildren,
        z: nextZ(),
    });
    image('l1', 1600, 64, 840, 64, 'nav — logo · 3 links · CTA', 840, 64);
    image('l2', 1600, 152, 840, 360, 'hero image', 840, 360);
    text('l3', 1640, 396, 560, 86, 'l', 'Tools for slow makers.');
    image('l4', 1600, 560, 260, 220, 'product card', 300, 240);
    image('l5', 1890, 560, 260, 220, 'product card', 300, 240);
    image('l6', 2180, 560, 260, 220, 'product card', 300, 240);
    image('l7', 1600, 880, 840, 120, 'footer — newsletter + socials', 840, 120);
    sticky('l8', 1604, 1020, '#F6D56A', 'hero copy lives in the Copy frame — keep in sync');
    // Two hand-drawn accents: a loose ellipse around the copy, and an underline.
    const ellipse = [];
    for (let t = 0; t <= Math.PI * 2.15; t += 0.16) {
        ellipse.push([
            185 + 178 * Math.cos(t) * (1 + Math.sin(t * 3) * 0.05),
            1165 + 52 * Math.sin(t) * (1 + Math.cos(t * 2.2) * 0.09),
        ]);
    }
    const underline = [];
    for (let x = 0; x <= 300; x += 12)
        underline.push([1644 + x, 492 + Math.sin(x / 22) * 5]);
    const strokes = [
        { id: 's1', pts: ellipse, color: '#A78BFA', w: 2.5 },
        { id: 's2', pts: underline, color: '#A78BFA', w: 2.5 },
    ];
    return {
        items,
        strokes,
        conns: [
            { id: 'k1', a: 'f1', b: 'f3' },
            { id: 'k2', a: 'c1', b: 'l3' },
            { id: 'k3', a: 'm6', b: 'l1' },
        ],
        comments: [
            {
                id: 'cm1',
                x: 516,
                y: 214,
                author: 'Maya',
                msgs: [
                    { id: 'cmm1', who: 'Maya', text: 'Love this texture — can we try a darker grade?', at: 1_735_689_600_000 },
                    { id: 'cmm2', who: 'You', text: 'On it for the next pass.', at: 1_735_693_200_000 },
                ],
            },
        ],
    };
}
//# sourceMappingURL=demo.js.map